import { useEffect } from "react";
import ReactDOM from "react-dom";

import { TEventHost } from "../../types";
import eventImage from "../../images/logo.png";
import { toSafeHtml } from "../../utils/html";
import styles from "./event-host-modal.module.css";

type Props = {
    host: TEventHost | null;
    isOpen: boolean;
    onClose: () => void;
};

const getPhotoUrl = (host: TEventHost) => {
    return host.image_urls?.small
        ?? host.image_urls?.avatar
        ?? eventImage;
};

const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.src = eventImage;
};

const EventHostModal = ({ host, isOpen, onClose }: Props) => {
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen || !host) {
        return null;
    }

    const modal = (
        <div className={styles.modalRoot}>
            <button
                type="button"
                className={styles.modalBackdrop}
                onClick={onClose}
                aria-label="Закрыть информацию о члене команды"
            />

            <div className={styles.hostModal} role="dialog" aria-modal="true" aria-label={`Информация: ${host.full_name}`}>
                <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Закрыть">
                    <svg width="18" height="18" viewBox="0 0 22 22" fill="none">
                        <path d="M1.5 20.5L20.5 1.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                        <path d="M1.5 1.5L20.5 20.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                </button>

                <div className={styles.photoBox}>
                    <img src={getPhotoUrl(host)} alt={host.full_name} onError={handleImageError} />
                    {typeof host.rating === "number" && (
                        <div className={styles.rating}>
                            {host.rating.toFixed(1)}
                        </div>
                    )}
                </div>

                <div className={styles.content}>
                    {host.role && <div className={styles.role}>{host.role}</div>}
                    <h2 className={styles.name}>{host.full_name}</h2>
                    <div className={styles.divider} />
                    {host.bio ? (
                        <div
                            className={styles.bio}
                            dangerouslySetInnerHTML={toSafeHtml(host.bio)}
                        />
                    ) : (
                        <div className={styles.empty}>Описание пока не добавлено</div>
                    )}
                </div>
            </div>
        </div>
    );

    const modalRoot = typeof document !== "undefined" ? document.getElementById("react-modals") : null;

    return modalRoot ? ReactDOM.createPortal(modal, modalRoot) : modal;
};

export default EventHostModal;
